/* LandingNav — shared sticky nav for the public marketing pages.
 *
 * Features
 *   • Identical link set on every page (Home · Features · Live CPA · Pricing)
 *     so navigating between / and /features never reflows the top bar.
 *   • Smart scroll-spy: when the user is on the landing page (`/`), the active
 *     link auto-highlights based on which section is currently in view. Driven
 *     by IntersectionObserver — no scroll-event spam.
 *   • Layout-stable active state. The label text uses a fixed `font-semibold`
 *     weight at all times and shows activeness via an underline pill BELOW the
 *     text rather than a bold-weight swap — so the link box width is identical
 *     whether it's active or inactive, and clicking between tabs never causes
 *     the nav row to shift horizontally.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';

// Order matters — this is the visual order on the bar.
const LINKS = [
  { id: 'home',     label: 'Home',     anchor: '',          // empty = top
                    sectionIds: ['top'] },
  { id: 'features', label: 'Features', anchor: '/features', external: true,
                    sectionIds: [] },
  { id: 'live-cpa', label: 'Live CPA', anchor: '#live-cpa',
                    sectionIds: ['live-cpa'] },
  { id: 'pricing',  label: 'Pricing',  anchor: '#pricing',
                    sectionIds: ['pricing'] },
];

export default function LandingNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === '/';
  const onFeatures = location.pathname === '/features';

  // Active link state. Defaults to "Home" on landing, "Features" on /features.
  const [active, setActive] = useState(onFeatures ? 'features' : 'home');

  // Scroll-spy — runs only on the landing page where the anchor sections live.
  // We resolve sections fresh on every recompute (cheap, ~2 getElementById calls)
  // so we don't have to wait for them to mount before attaching the listener.
  useEffect(() => {
    if (!onLanding) return undefined;
    let raf = 0;

    const recompute = () => {
      raf = 0;
      const sectionIds = ['live-cpa', 'pricing'];
      const probe = window.innerHeight * 0.35;
      let current = 'home';
      if (window.scrollY > 200) {
        const rects = sectionIds
          .map((id) => {
            const el = document.getElementById(id);
            return el ? { id, rect: el.getBoundingClientRect() } : null;
          })
          .filter(Boolean);
        for (const { id, rect } of rects) {
          if (rect.top <= probe && rect.bottom >= probe) {
            current = id;
            break;
          }
        }
        if (current === 'home') {
          // Fallback: pick the LAST section whose top is above the probe.
          for (let i = rects.length - 1; i >= 0; i -= 1) {
            if (rects[i].rect.top < probe) {
              current = rects[i].id;
              break;
            }
          }
        }
      }
      setActive(current);
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(recompute);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Initial computation. Use rAF so sections that mount in the same tick are visible.
    raf = requestAnimationFrame(recompute);
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [onLanding]);

  const handleClick = (link) => (e) => {
    if (link.external) return; // <Link to="/features"> handles it
    e.preventDefault();
    if (!onLanding) {
      // We're on /features and the link is an anchor on the landing page.
      navigate('/' + link.anchor);
      return;
    }
    if (link.id === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActive('home');
      return;
    }
    const el = document.querySelector(link.anchor);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(link.id);
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <img src="/countbucks-icon.png" alt="CountBucks" className="w-9 h-9" />
          <span className="font-bold tracking-tight text-white">CountBucks</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {LINKS.map((link) => {
            const isActive = active === link.id;
            const common = 'relative px-4 py-2 text-sm font-semibold text-zinc-300 hover:text-white transition-colors';
            const inner = (
              <>
                <span className={isActive ? 'text-white' : ''}>{link.label}</span>
                {/* Stable underline pill — fixed height, opacity-toggled so width never changes */}
                <span
                  aria-hidden
                  className={[
                    'absolute left-3 right-3 bottom-1 h-[2px] rounded-full bg-brand-400 transition-all duration-200',
                    isActive ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-50',
                  ].join(' ')}
                />
              </>
            );
            if (link.external) {
              return (
                <Link
                  key={link.id}
                  to={link.anchor}
                  className={common}
                  data-testid={`nav-${link.id}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {inner}
                </Link>
              );
            }
            return (
              <a
                key={link.id}
                href={onLanding ? (link.anchor || '#top') : '/' + link.anchor}
                onClick={handleClick(link)}
                className={common}
                data-testid={`nav-${link.id}`}
                aria-current={isActive ? 'page' : undefined}
              >
                {inner}
              </a>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-login-btn">
            <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-white hover:bg-white/5">
              Sign in
            </Button>
          </Link>
          <Link to="/login?mode=register&plan=starter&cadence=monthly" data-testid="nav-cta-btn">
            <Button size="sm" className="bg-white text-black hover:bg-zinc-200 gap-1.5 font-semibold">
              Get Started <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}
